import * as React from "react";
import { Calendar as CalendarIcon } from "lucide-react";
import { format, parse, isValid } from "date-fns";
import { de } from "date-fns/locale";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";

/**
 * Deutsche Datumsauswahl im Kino-Look. Bindung nach außen bleibt ISO (YYYY-MM-DD).
 * Anzeige: TT/MM/JJJJ (EU-Format), Kalender in Gold/Schwarz.
 */
export default function CinemaDatePicker({ value, onChange, testid = "cinema-date" }) {
  const [open, setOpen] = React.useState(false);
  const parsed = value ? parse(value, "yyyy-MM-dd", new Date()) : null;
  const selected = parsed && isValid(parsed) ? parsed : undefined;
  const label = selected ? format(selected, "EEEE, dd/MM/yyyy", { locale: de }) : "— Datum wählen —";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 flex items-center justify-between text-left hover:border-[#D4AF37] focus:border-[#D4AF37] outline-none transitions-safe"
        >
          <span className={selected ? "text-[#F5F0E6]" : "text-[#575148]"}>{label}</span>
          <CalendarIcon size={16} className="text-[#D4AF37]" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="p-0 bg-[#0A0A0A] border border-[#2A2A2A] rounded-lg shadow-[0_20px_60px_rgba(0,0,0,0.6)] w-auto"
        data-testid={`${testid}-popover`}
      >
        <Calendar
          mode="single"
          locale={de}
          weekStartsOn={1}
          selected={selected}
          onSelect={(d) => {
            if (d) {
              onChange(format(d, "yyyy-MM-dd"));
              setOpen(false);
            }
          }}
          initialFocus
          className="p-3 text-[#F5F0E6]"
          classNames={{
            months: "flex flex-col",
            month: "space-y-3",
            caption: "flex justify-center pt-1 relative items-center",
            caption_label: "font-heading text-lg text-[#D4AF37] tracking-wider",
            nav: "space-x-1 flex items-center",
            nav_button: "h-7 w-7 bg-transparent p-0 border border-[#2A2A2A] rounded hover:border-[#D4AF37] text-[#A8A398] hover:text-[#D4AF37] flex items-center justify-center transitions-safe",
            nav_button_previous: "absolute left-1",
            nav_button_next: "absolute right-1",
            table: "w-full border-collapse",
            head_row: "flex",
            head_cell: "text-[#575148] rounded-md w-9 font-normal text-[10px] uppercase tracking-widest",
            row: "flex w-full mt-1",
            cell: "relative p-0 text-center text-sm focus-within:z-20",
            day: "h-9 w-9 p-0 font-mono text-sm rounded-md text-[#F5F0E6] hover:bg-[#1A1A1A] hover:text-[#D4AF37] transitions-safe aria-selected:opacity-100",
            day_selected: "bg-[#D4AF37] text-black hover:bg-[#EEDC82] hover:text-black focus:bg-[#D4AF37] focus:text-black",
            day_today: "border border-[#D4AF37]/50 text-[#D4AF37]",
            day_outside: "text-[#3A3630] opacity-60",
            day_disabled: "text-[#3A3630] opacity-40",
            day_hidden: "invisible",
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
